// Single source of truth for the AXIS site: services, WhatsApp links, copy, SEO.
import { GOOGLE_FORM_URL, EXPERIENCE_BLUEPRINT_FORM_URL, CHARACTER_BRANDING_FORM_URL } from "../config";

export const SITE_URL = "https://axisbzsolutions.com";
export const WHATSAPP_NUMBER = "12424264889";

// Google Forms kept as post-qualification / fallback intake only.
export const GOOGLE_FORM_FALLBACKS = {
  launchflow: GOOGLE_FORM_URL,
  "character-branding": CHARACTER_BRANDING_FORM_URL,
  "experience-blueprint": EXPERIENCE_BLUEPRINT_FORM_URL,
};

const wa = (text) => `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;

export const WA_LINKS = {
  recommend: wa(
    "Hi%20AXIS.%20I%20would%20like%20a%20service%20recommendation.%20The%20main%20thing%20I%20am%20trying%20to%20improve%20is%3A"
  ),
  launch: wa(
    "LAUNCH%20%E2%80%94%20I%20would%20like%20an%20AXIS%20Visibility%20Check%20and%20a%20LaunchFlow%20recommendation."
  ),
  character: wa(
    "CHARACTER%20%E2%80%94%20I%20would%20like%20to%20explore%20an%20AXIS%20Character%20Branding%20project."
  ),
  blueprint: wa(
    "BLUEPRINT%20%E2%80%94%20I%20would%20like%20an%20AXIS%20Quick%20Look%20for%20my%20customer%20experience."
  ),
};

export const PROBLEM_SELECTOR = [
  {
    id: "cant-find-me",
    problem: "Customers cannot find or contact me easily",
    blurb: "Your business relies on social media, repeated directions, scattered information, or manual answers to basic customer questions.",
    service: "LaunchFlow™",
    cta: "Explore LaunchFlow",
    to: "/services/launchflow",
  },
  {
    id: "memorable-asset",
    problem: "I want a brand asset people remember",
    blurb: "You want a recognizable visual spokesperson that can explain, teach, remind, or promote consistently.",
    service: "Character Branding™",
    cta: "Explore Character Branding",
    to: "/services/character-branding",
  },
  {
    id: "customer-experience",
    problem: "My customer experience needs improvement",
    blurb: "Something feels inconsistent in the physical environment, customer journey, presentation, or service flow—but the source of the friction is unclear.",
    service: "Experience Blueprint™",
    cta: "Explore the Experience Blueprint",
    to: "/services/experience-blueprint",
  },
];

export const HOW_AXIS_WORKS = [
  { n: "01", title: "Fit check", blurb: "Tell us what you are trying to improve and answer three short questions through WhatsApp." },
  { n: "02", title: "AXIS recommendation", blurb: "We recommend one service or advise you when the timing or fit is not right." },
  { n: "03", title: "Scope and booking", blurb: "You receive the defined deliverables, investment, timeline, exclusions, agreement, and deposit requirement." },
  { n: "04", title: "Focused intake", blurb: "We collect only the information and assets required for the approved engagement." },
  { n: "05", title: "Delivery and handoff", blurb: "AXIS completes the work, manages the agreed review process, and provides the finished deliverables or roadmap." },
];

export const WHY_AXIS_POINTS = [
  "We define the problem before recommending the service.",
  "We connect strategy to practical customer-facing assets.",
  "We establish scope, timelines, revisions, and exclusions before work begins.",
  "We use AI to support analysis and production—not replace judgment.",
  "We build tools the client can actually use after handoff.",
];

export const TRUST_BAR = [
  "20+ years of experience",
  "Strategy and execution",
  "Mobile-friendly customer journeys",
  "Built for operator-led businesses",
];

export const SERVICES = [
  {
    slug: "launchflow",
    name: "AXIS LaunchFlow™",
    shortName: "LaunchFlow",
    summary: "Give your business a professional digital front door that helps customers find you, understand the offer, build trust, and take the next step.",
    priceLine: "Projects from B$360",
    hubBestFor: "When customers need one reliable place to find, understand, trust, and contact the business",
    fitGuidance: {
      title: "Start with LaunchFlow when:",
      items: [
        "Customers repeatedly ask for hours, location, services, prices, directions, or contact details.",
        "The business relies entirely on social media.",
        "You need one focused digital destination rather than a large website.",
      ],
    },
    seo: {
      title: "AXIS LaunchFlow | A Professional Digital Front Door for Your Business",
      description: "Launch a focused, mobile-friendly digital destination that helps customers find your business, understand the offer, build trust, and contact you.",
    },
    eyebrow: "AXIS LaunchFlow™",
    h1: "Give your business a professional digital front door.",
    positioning: [
      "LaunchFlow helps customers find you, understand what you offer, trust the business, and take the next step—all from one focused, mobile-friendly destination.",
    ],
    investment: "Projects from B$360",
    waLink: WA_LINKS.launch,
    waLabel: "Message \u201CLAUNCH\u201D on WhatsApp",
    secondaryCtaLabel: "Get a Visibility Check",
    problem: {
      title: "The problem",
      intro: "Many businesses do not need a large, complicated website. They need one reliable place where customers can quickly find:",
      items: [
        "Who the business is",
        "What it offers",
        "Where it is located",
        "When it is open",
        "Why customers should trust it",
        "How to call, WhatsApp, book, request a quote, or visit",
      ],
      outro: "LaunchFlow solves that visibility and customer-action problem.",
    },
    bestFor: [
      "Businesses relying entirely on social media",
      "Businesses repeatedly answering questions about hours, location, services, or contact information",
      "Service providers that need a professional destination for referrals and advertising",
      "Businesses that need to launch quickly without commissioning a large website",
      "Restaurants, salons, barbers, contractors, consultants, realtors, photographers, retailers, event companies, churches, medical offices, vacation rentals, and other operator-led businesses",
    ],
    deliverables: [
      {
        title: "Standard LaunchFlow structure",
        items: [
          "Outcome-led hero section",
          "Business introduction",
          "Services, products, or packages",
          "Reasons to choose the business",
          "Gallery",
          "Testimonials",
          "Frequently asked questions",
          "Hours, address, and service area",
          "Google Maps connection",
          "WhatsApp, phone, email, booking, or quote pathway",
          "Final call to action",
          "Mobile optimization",
          "QR-code-ready destination",
        ],
      },
    ],
    packagesTitle: "Public packages",
    packages: [
      {
        name: "LaunchFlow Professional",
        price: "Starting at B$650",
        items: [
          "Focused page strategy",
          "Mobile-first single-page build",
          "Enhanced section layout",
          "Services or packages section",
          "Gallery",
          "Testimonials",
          "Frequently asked questions",
          "Multiple calls to action",
          "Existing domain connection",
          "Two consolidated revision rounds",
          "Launch checklist and handoff",
        ],
      },
      {
        name: "LaunchFlow Premium",
        price: "Starting at B$950",
        blurb: "Includes LaunchFlow Professional, plus:",
        items: [
          "Strategy consultation",
          "Premium copy refinement",
          "Analytics setup",
          "Campaign-ready landing-page structure",
          "Expansion-ready technical foundation",
          "30-day launch review",
        ],
      },
    ],
    addons: {
      title: "Optional add-ons",
      items: [
        "Domain setup",
        "Business email setup",
        "Google Business Profile support",
        "QR-code application package",
        "Booking or payment-link integration",
        "Additional sections",
        "Analytics and reporting",
        "Professional copywriting",
      ],
    },
    notIncluded: [
      "Multi-page websites",
      "E-commerce or online stores",
      "Membership portals or client logins",
      "CRM systems",
      "Staff portals",
      "Workflow automation",
      "Dashboards",
      "Custom databases",
      "Complex booking-management systems",
      "Unlimited maintenance or revisions",
    ],
    boundary: [
      "These requirements must be separately scoped.",
      "Where included, the 30-day review covers available visitor and inquiry information, repeated customer questions, CTA performance, visibility gaps, and recommended improvements.",
    ],
    gallery: {
      title: "What you receive",
      fullImage: true,
      images: [
        { src: "/assets/launchflow/01-what-you-receive.png", alt: "AXIS LaunchFlow — a professional digital front door for your business. Get found, build trust, get contacted." },
        { src: "/assets/launchflow/02-focused-page-strategy.png", alt: "LaunchFlow step 01 — Focused page strategy: one mobile-first single-page build" },
        { src: "/assets/launchflow/03-build-customer-trust.png", alt: "LaunchFlow step 02 — Build customer trust: reasons to choose you, gallery, testimonials, FAQs" },
        { src: "/assets/launchflow/04-make-contact-easy.png", alt: "LaunchFlow step 03 — Make contact easy: WhatsApp, phone, email, maps, booking pathway" },
        { src: "/assets/launchflow/05-launch-and-handoff.png", alt: "LaunchFlow step 04 — Launch and handoff: domain connection, revisions, launch checklist, QR-ready" },
      ],
    },
    finalCta: {
      h2: "Can customers find, trust, and contact your business?",
      blurb: "Message AXIS for a short Visibility Check. We will identify the most important gap and recommend one appropriate LaunchFlow level.",
    },
  },
  {
    slug: "character-branding",
    name: "AXIS Character Branding™",
    shortName: "Character Branding",
    summary: "Create a recognizable visual spokesperson and the strategic rules needed to use it consistently.",
    priceLine: "Packages from B$150",
    hubBestFor: "When the organization needs a recognizable visual spokesperson",
    fitGuidance: {
      title: "Start with Character Branding when:",
      items: [
        "A character would help the organization explain, teach, remind, or promote.",
        "You need more than a one-off illustration.",
        "The character must remain consistent across repeated communications.",
      ],
    },
    seo: {
      title: "AXIS Character Branding | Create a Recognizable Visual Spokesperson",
      description: "Create a recognizable visual spokesperson with a defined role, personality, voice, applications, usage guidance, and consistency system.",
    },
    eyebrow: "AXIS Character Branding™",
    h1: "Not just a mascot. A reusable communication tool.",
    positioning: [
      "AXIS creates recognizable visual spokespeople and the strategic rules needed to use them consistently across campaigns, customer education, staff communication, social media, print, and other approved touchpoints.",
    ],
    investment: "Packages from B$150",
    waLink: WA_LINKS.character,
    waLabel: "Message \u201CCHARACTER\u201D on WhatsApp",
    secondaryCtaLabel: "View Character Examples",
    secondaryCtaTo: "/case-studies",
    extraList: {
      title: "What makes this different",
      intro: "A character becomes valuable when it has a clear job to do. AXIS defines:",
      items: [
        "Purpose and audience",
        "Communication role",
        "Appearance and distinctive features",
        "Personality and voice",
        "Expressions, poses, and behavior",
        "Approved and prohibited uses",
        "Application scenarios",
        "Consistency and quality-control guidance",
      ],
      outro: "The selected package determines the depth of strategy, assets, guidance, and usage rights.",
    },
    bestFor: [
      "Organizations that need a recognizable spokesperson",
      "Customer education and public-information campaigns",
      "Schools and educational programs",
      "Hospitality and tourism communication",
      "Internal staff communication",
      "Recurring promotional campaigns",
      "Brands that need a character to explain, teach, remind, or promote",
    ],
    receiveGallery: {
      title: "What you receive",
      fullImage: true,
      images: [
        { src: "/assets/character-branding/01-what-you-receive.png", alt: "AXIS Character Branding — what you receive: a recognizable visual spokesperson with rules for consistent use" },
        { src: "/assets/character-branding/02-role-and-personality.png", alt: "Character Branding step 01 — Role & Personality: purpose, audience, communication role, personality, voice, appearance" },
        { src: "/assets/character-branding/03-character-assets.png", alt: "Character Branding step 02 — Character Assets: hero pose, profile avatar, poses and expressions, signature lines" },
        { src: "/assets/character-branding/04-usage-and-consistency.png", alt: "Character Branding step 03 — Usage & Consistency: approved and prohibited uses, voice guidance, application scenarios, usage guide" },
        { src: "/assets/character-branding/05-choose-your-level.png", alt: "Character Branding step 04 — Choose Your Level: Essentials B$150, Foundation B$200, Communication System B$350, Brand World from B$500" },
      ],
    },
    packagesTitle: "Package ladder",
    packages: [
      {
        name: "Character Essentials",
        price: "B$150",
        blurb: "For one professionally directed character asset and one immediate digital communication need.",
        items: [
          "One focused character direction",
          "One finished hero pose or expression",
          "One-sentence role and purpose",
          "One signature line",
          "One profile avatar",
          "One-page usage card",
          "Limited single-organization digital-use licence",
          "One consolidated revision round",
        ],
        note: "Does not include Character DNA™, additional poses, print use, animation, merchandise, source files, resale rights, or unrestricted ownership.",
      },
      {
        name: "Character Foundation",
        price: "Starting at B$200",
        blurb: "For a stronger working spokesperson with enough variety for repeated introductory use.",
        items: [
          "Character strategy and creative direction",
          "Role and personality",
          "Brand voice and three catchphrases",
          "Three poses or expressions",
          "Profile avatar",
          "Basic usage guide",
          "Standard digital-use licence",
          "One consolidated revision round",
        ],
      },
      {
        name: "Character Communication System",
        price: "Starting at B$350",
        popular: true,
        blurb: "For recurring communication, broader internal use, and consistent campaign applications.",
        items: [
          "Everything in Character Foundation",
          "AXIS Character DNA™",
          "Six to eight total poses or expressions",
          "Expanded voice and behavior guidance",
          "Approved and prohibited behaviors",
          "Campaign and communication scenarios",
          "Starter templates",
          "Expanded usage guide",
          "Handoff session",
          "Two consolidated revision rounds",
        ],
      },
      {
        name: "Character Brand World",
        price: "Starting at B$500",
        blurb: "For multi-channel, department, campaign, team, and vendor deployment.",
        items: [
          "Everything in the Character Communication System",
          "Twelve to sixteen total poses, expressions, or scenarios",
          "Expanded Character DNA™ production rules",
          "Campaign concept and launch direction",
          "Selected print and digital applications",
          "Launch-ready asset library",
          "AI-assisted production-consistency guidance",
          "Team and vendor handoff",
          "Defined quality-control and approval process",
          "Commercial licence scoped to the approved organization and channels",
        ],
      },
    ],
    process: [
      "Three-question WhatsApp fit check",
      "One recommended Character Branding level",
      "Defined scope, licence, agreement, and deposit",
      "Focused character intake",
      "Strategy and visual-direction development",
      "Approved production and review rounds",
      "Final payment, asset delivery, usage guidance, and handoff",
    ],
    licensing: {
      title: "Licensing",
      intro: "Every proposal must define:",
      items: [
        "Licensed organization",
        "Approved purpose and channels",
        "Territory where relevant",
        "Duration where limited",
        "Exclusivity",
        "Modification rights",
        "Resale and merchandise rights",
        "Use by affiliated organizations or vendors",
      ],
      outro: "No package automatically includes unrestricted ownership, resale rights, merchandise rights, source files, or use by multiple organizations.",
    },
    addons: {
      title: "Add-ons",
      items: [
        "Additional poses, expressions, outfits, or scenarios",
        "Posters, signs, decals, banners, and printed materials",
        "Social-media content packs",
        "Training and internal-communication graphics",
        "Campaign applications",
        "Animation and video",
        "Additional characters",
        "Merchandise and product licensing",
        "Expanded commercial rights",
        "Ongoing character content support",
      ],
    },
    gallery: {
      title: "Character work in the wild",
      images: [
        { src: "/assets/case-studies/wig-doctor-page-1.png", alt: "The Wig Doctor — character brand system featuring Dr. Mully" },
        { src: "/assets/case-studies/loft-cover.png", alt: "The Authentic Loft — character brand system featuring Arielle" },
        { src: "/assets/character-branding/idea-to-icon.png", alt: "AXIS Character Branding — from idea to icon process example" },
      ],
    },
    finalCta: {
      h2: "What should your character help you explain, teach, remind, or promote?",
      blurb: "Answer three short questions. AXIS will recommend one appropriate starting level—not send you an overwhelming package menu.",
    },
  },
  {
    slug: "experience-blueprint",
    name: "The AXIS Experience Blueprint™",
    shortName: "Experience Blueprint",
    summary: "Document how customers experience your business and turn those observations into a prioritized roadmap for improvement.",
    priceLine: "Engagements from B$150",
    hubBestFor: "When the customer journey or physical business experience needs structured assessment",
    fitGuidance: {
      title: "Start with the Experience Blueprint when:",
      items: [
        "The business has physical customer touchpoints.",
        "The owner knows something is not working but cannot isolate the source.",
        "Improvements may involve environment, signage, presentation, service flow, or vendor coordination.",
      ],
    },
    seo: {
      title: "AXIS Experience Blueprint | Customer Experience Assessment Bahamas",
      description: "A professional on-site assessment that documents how customers experience your business and turns the findings into a prioritized improvement roadmap.",
    },
    eyebrow: "The AXIS Experience Blueprint™",
    h1: "See your business the way your customers experience it.",
    positioning: [
      "The Experience Blueprint is a professional on-site assessment that documents customer-facing reality, identifies friction and missed opportunities, and turns those findings into a practical roadmap for improvement.",
    ],
    investment: "Engagements from B$150",
    waLink: WA_LINKS.blueprint,
    waLabel: "Start With an AXIS Quick Look",
    secondaryCtaLabel: "View Experience Blueprint Work",
    secondaryCtaTo: "/case-studies",
    bestFor: [
      "Retail, hospitality, restaurants, salons, professional services, and other customer-facing environments",
      "Business owners who know something is not working but cannot isolate the source",
      "Businesses preparing for a physical refresh or operational improvement",
      "Organizations with inconsistent signage, presentation, service flow, atmosphere, or customer touchpoints",
    ],
    methodology: [
      { title: "Observe", blurb: "AXIS documents the business as a customer experiences it—without beginning from assumption." },
      { title: "Analyze", blurb: "The customer journey, identity, flow, atmosphere, environment, signage, and presentation are reviewed through structured lenses." },
      { title: "Document", blurb: "Findings are organized into a professional Blueprint that establishes a clear source of truth." },
      { title: "Visualize", blurb: "Where appropriate, AXIS creates clearly labeled Concept Visualizations to help the client understand a possible future direction. These are strategic recommendations—not photographs." },
      { title: "Recommend", blurb: "Opportunities are prioritized into a practical 30/60/90-day roadmap." },
      { title: "Implement", blurb: "Implementation support, vendor coordination, production oversight, or installation can be separately scoped after the Blueprint is approved." },
    ],
    deliverables: [
      {
        title: "Core deliverables",
        items: [
          "Professional on-site visit",
          "Customer-journey documentation",
          "Structured customer-experience audit",
          "Opportunity scoring",
          "Visual friction map",
          "Prioritized recommendations",
          "30/60/90-day action plan",
          "AXIS Concept Visualizations where included",
          "Implementation recommendation",
          "Client handoff",
        ],
      },
    ],
    boundary: [
      "The Blueprint diagnoses, documents, prioritizes, and recommends.",
      "The starting investment depends on the size, complexity, location, and required deliverables.",
      "Implementation is not automatically included in the assessment price.",
      "Vendor costs, fabrication, materials, installation, renovations, permits, and professional services are separately quoted.",
      "AI-assisted visuals are clearly labeled and do not represent guaranteed final construction outcomes.",
    ],
    philosophy: {
      h2: "Evidence before ideas.",
      blurb: "AXIS documents reality before proposing transformation. AI supports analysis and visualization. Human expertise drives every recommendation.",
    },
    receiveGallery: {
      title: "What you receive",
      fullImage: true,
      images: [
        { src: "/assets/experience-blueprint/01-what-you-receive.png", alt: "AXIS Experience Blueprint — what you receive: a site-specific improvement plan for your customer experience" },
        { src: "/assets/experience-blueprint/02-scheduled-site-visit.png", alt: "Experience Blueprint step 01 — Scheduled site visit: one Nassau site visit of up to 60 minutes" },
        { src: "/assets/experience-blueprint/03-two-priority-touchpoints.png", alt: "Experience Blueprint step 02 — Two priority touchpoints: assessment of up to two customer-facing touchpoints" },
        { src: "/assets/experience-blueprint/04-concept-visualizations.png", alt: "Experience Blueprint step 03 — Concept visualizations: up to two proposed site-specific concept visualizations" },
        { src: "/assets/experience-blueprint/05-your-action-plan.png", alt: "Experience Blueprint step 04 — Your action plan: annotated recommendations, prioritized plan, presentation call and one revision round" },
      ],
    },
    gallery: {
      title: "AXIS Concept Visualizations",
      note: "Clearly labeled strategic visualizations — not photographs or guaranteed construction outcomes.",
      images: [
        { src: "/assets/case-studies/eg-page-1.png", alt: "AXIS Concept Visualization — Executive Grooming frontage analysis", label: "AXIS Concept Visualization" },
        { src: "/assets/case-studies/eg-page-2.png", alt: "AXIS Concept Visualization — Executive Grooming interior atmosphere", label: "AXIS Concept Visualization" },
        { src: "/assets/case-studies/eg-page-3.png", alt: "AXIS Concept Visualization — Executive Grooming concept direction", label: "AXIS Concept Visualization" },
      ],
    },
    finalCta: {
      h2: "Ready to see your business differently?",
      blurb: "Start with a focused AXIS Quick Look. We will discuss the business, location, customer experience, and whether an on-site Blueprint is the right next step.",
    },
  },
];

export const getServiceBySlug = (slug) => SERVICES.find((s) => s.slug === slug);

export const CONTACT_KEYWORDS = [
  { keyword: "LAUNCH", desc: "for a professional digital front door", link: WA_LINKS.launch },
  { keyword: "CHARACTER", desc: "for a reusable visual spokesperson", link: WA_LINKS.character },
  { keyword: "BLUEPRINT", desc: "for an on-site customer-experience assessment", link: WA_LINKS.blueprint },
  { keyword: "RECOMMEND", desc: "if you are unsure", link: WA_LINKS.recommend },
];

export const PAGE_SEO = {
  home: {
    title: "AXIS Business Solutions | Brand, Campaign and Customer Experience Services Bahamas",
    description: "AXIS helps Bahamian organizations launch professional digital experiences, create recognizable character brands, and improve customer experience.",
    ogTitle: "AXIS Business Solutions | Clearer Brands. Stronger Customer Experiences.",
    ogDescription: "Practical strategy, campaigns, digital launch systems, character branding, and customer-experience consulting for growing organizations.",
    path: "/",
  },
  services: {
    title: "AXIS Services | LaunchFlow, Character Branding and Customer Experience",
    description: "Explore three focused AXIS services for digital visibility, character branding, and customer-experience improvement.",
    path: "/services",
  },
  caseStudies: {
    title: "AXIS Case Studies | Real Businesses. Real Transformations.",
    description: "Explore AXIS Experience Blueprint and Character Branding case studies from real Bahamian businesses — problem, service, deliverables, and outcome.",
    path: "/case-studies",
  },
  about: {
    title: "About AXIS Business Solutions | Strategy, Design and Customer Experience",
    description: "Meet Davin Campbell and learn how AXIS combines strategic marketing, visual communication, customer experience, operational judgment, and AI-supported production.",
    path: "/about",
  },
  contact: {
    title: "Contact AXIS Business Solutions | Get a Service Recommendation",
    description: "Tell AXIS what you are trying to improve. Answer three short questions on WhatsApp and receive one recommended starting point.",
    path: "/contact",
  },
};
